import { FC, PropsWithChildren, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { TabPanes, Topbar, PageLoader, toast } from "@vilocnv/allsetra-core";
import { Box, useTheme } from "@mui/material";
import { Add, Settings } from "@mui/icons-material";
import { PageLoaderCentered } from "components/sections/objects/ObjectDetailsSection/ObjectDetailsSection.styled";
import AddMaintenanceAlertRuleModal from "./AddMaintenanceAlertRuleModal";
import { MAINTENANCE_ALERT_RULES_UPDATED_EVENT } from "components/common/objects/MaintenanceAlerts/maintenanceAlertsEvents";

// Data
import { useActiveObjectById, useAppDispatch, useAppSelector } from "hooks";
import { getObjectFeaturesThunk, setObjectActiveTabIndex } from "app/features";
import {
  selectDrawerSelectedAccountId,
  selectIsDrawerCollapsed,
  selectObjectActiveTabIndex,
  selectObjectFeaturesState
} from "app/data/selectors";
import {
  getObjectsDetailsTabRouteNames,
  OBJECTS_DETAILS_TABS_HEADINGS
} from "app/data/constants";
import { isMonitoringTabEnabled } from "app/data/helpers";
import { useTranslation } from "react-i18next";

const ObjectTabsLayout: FC<PropsWithChildren> = ({ children }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useAppDispatch();
  const location = useLocation();

  // Global State
  const isDrawerCollapsed = useAppSelector(selectIsDrawerCollapsed);
  const objectActiveTabIndex = useAppSelector(selectObjectActiveTabIndex);
  const drawerSelectedAccountId = useAppSelector(selectDrawerSelectedAccountId);
  const { objectFeaturesConfig, objectFeaturesLoading } = useAppSelector(
    selectObjectFeaturesState
  );

  const { specificObject, loading } = useActiveObjectById();

  const { t } = useTranslation();
  const isMonitoringEnabled = isMonitoringTabEnabled(drawerSelectedAccountId);
  const isMaintenanceAlertsTab =
    location.pathname.includes("maintenance-alerts");
  const [isAddAlertRuleModalOpen, setIsAddAlertRuleModalOpen] = useState(false);

  const tabsHeadings = useMemo(
    () =>
      OBJECTS_DETAILS_TABS_HEADINGS(
        t,
        objectFeaturesConfig,
        isMonitoringEnabled
      ),
    [t, objectFeaturesConfig, isMonitoringEnabled]
  );

  const tabRouteNames = useMemo(
    () =>
      getObjectsDetailsTabRouteNames(
        objectFeaturesConfig.isRidesEnabled,
        isMonitoringEnabled
      ),
    [objectFeaturesConfig.isRidesEnabled, isMonitoringEnabled]
  );

  useEffect(() => {
    if (location.pathname.includes("monitoring") && !isMonitoringEnabled) {
      navigate(`/dashboard/objects/details/${params.id}`, { replace: true });
      return;
    }

    const nextIndex = tabRouteNames.findIndex((routeName: string) =>
      location.pathname.includes(routeName)
    );

    if (nextIndex !== -1) {
      dispatch(setObjectActiveTabIndex(nextIndex));
    }
  }, [
    location.pathname,
    tabRouteNames,
    dispatch,
    isMonitoringEnabled,
    navigate,
    params.id
  ]);

  useEffect(() => {
    const fetchSpecificObjectFeatures = async () => {
      const { type } = await dispatch(
        getObjectFeaturesThunk({
          accountId: drawerSelectedAccountId ?? "",
          objectId: params.id ?? ""
        })
      );

      if (type === "objects/getObjectFeaturesThunk/rejected") {
        navigate(-1);
        toast.error("Object features were not found.");
      }
    };

    fetchSpecificObjectFeatures();
  }, [drawerSelectedAccountId, params.id, dispatch, navigate]);

  const onChangeTab = (value: number) => {
    dispatch(setObjectActiveTabIndex(value));
    const routeName = tabRouteNames[value] || "details";
    navigate(`/dashboard/objects/${routeName}/${params.id}`);
  };

  return (
    <Box>
      {loading || objectFeaturesLoading ? (
        <PageLoaderCentered isDrawerCollapsed={isDrawerCollapsed}>
          <PageLoader />
        </PageLoaderCentered>
      ) : (
        <Box>
          <Topbar
            theme={theme}
            title={specificObject?.name || "Object"}
            breadcrumbTitle={t("drawerMenuLinks.objects")}
            breadcrumbRedirectTo={() => navigate("/dashboard/objects")}
            primaryButton={{
              id: "object-settings",
              variant: "outlined",
              text: t("buttonsText.manageObject"),
              onClick: () =>
                navigate(
                  `/dashboard/objects/${specificObject?.uniqueId}/settings`
                ),
              startIcon: <Settings />,
              sx: {
                border: "1px solid #00B2A3 !important"
              }
            }}
            secondaryButton={
              isMaintenanceAlertsTab
                ? {
                    id: "add-maintenance-alert-rule",
                    variant: "contained",
                    text: t("maintenanceAlerts.addAlertRule"),
                    onClick: () => setIsAddAlertRuleModalOpen(true),
                    startIcon: <Add />,
                    sx: {
                      backgroundColor: "#00B2A3",
                      color: "#FFFFFF",
                      marginLeft: "10px"
                    }
                  }
                : undefined
            }
          />
          <Box
            sx={{
              "& .MuiTabs-root": {
                "& .MuiTab-root.Mui-selected": {
                  color: "#845AFC"
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#845AFC"
                }
              }
            }}
          >
            <TabPanes
              value={objectActiveTabIndex}
              onChange={onChangeTab}
              headings={tabsHeadings}
            >
              <Box mx={2}>{children}</Box>
            </TabPanes>
          </Box>
        </Box>
      )}
      <AddMaintenanceAlertRuleModal
        open={isAddAlertRuleModalOpen}
        onClose={() => setIsAddAlertRuleModalOpen(false)}
        accountId={drawerSelectedAccountId || ""}
        defaultObject={
          specificObject
            ? { uniqueId: specificObject.uniqueId, name: specificObject.name }
            : null
        }
        onSuccess={(result) => {
          window.dispatchEvent(
            new CustomEvent(MAINTENANCE_ALERT_RULES_UPDATED_EVENT, {
              detail: result
            })
          );
        }}
      />
    </Box>
  );
};

export default ObjectTabsLayout;
